import { Controller } from "@hotwired/stimulus"

// A typeable single-select picker.
//
// All candidate options are pre-rendered server-side; the controller does
// in-memory substring matching as the user types. Designed for lists of
// ~hundreds (e.g. features of a car). For larger sets, swap to a debounced
// remote search.
//
// HTML shape:
//   <div data-controller="searchable-select">
//     <input type="text" data-searchable-select-target="query" />
//     <input type="hidden" name="feature_id" data-searchable-select-target="hidden" />
//     <ul data-searchable-select-target="dropdown" hidden>
//       <li data-searchable-select-target="option"
//           data-action="mousedown->searchable-select#select"
//           data-value="<uuid>"
//           data-label="..."
//           data-search="lowercased searchable text">
//         <span class="label">...</span>
//       </li>
//       ...
//     </ul>
//   </div>
export default class extends Controller {
  static targets = ["query", "hidden", "dropdown", "option", "empty"]

  connect() {
    this.element.addEventListener("focusin", this.handleFocusIn)
    this.element.addEventListener("focusout", this.handleFocusOut)
  }

  disconnect() {
    this.element.removeEventListener("focusin", this.handleFocusIn)
    this.element.removeEventListener("focusout", this.handleFocusOut)
  }

  handleFocusIn = () => {
    this.open()
  }

  handleFocusOut = (event) => {
    // Wait a tick so a click on an option fires before we close.
    setTimeout(() => {
      if (!this.element.contains(document.activeElement)) {
        this.close()
      }
    }, 100)
  }

  open() {
    this.dropdownTarget.hidden = false
    this.filter()
  }

  close() {
    this.dropdownTarget.hidden = true
  }

  filter() {
    const raw = (this.queryTarget.value || "").trim().toLowerCase()
    let visibleCount = 0

    this.optionTargets.forEach((opt) => {
      const haystack = (opt.dataset.search || opt.textContent || "").toLowerCase()
      const match = raw === "" || haystack.includes(raw)
      opt.hidden = !match
      if (match) visibleCount++
    })

    if (this.hasEmptyTarget) {
      this.emptyTarget.hidden = visibleCount > 0
    }
  }

  select(event) {
    event.preventDefault()
    const opt = event.currentTarget
    this.hiddenTarget.value = opt.dataset.value || ""
    this.hiddenTarget.dataset.label = opt.dataset.label || opt.textContent.trim()
    this.queryTarget.value = opt.dataset.label || opt.textContent.trim()
    this.close()
    this.hiddenTarget.dispatchEvent(new Event("change", { bubbles: true }))
    // Lose focus so the dropdown actually stays closed.
    this.queryTarget.blur()
  }

  // Allows the page to reset the picker (used after a successful submit
  // re-renders the turbo frame with a new candidate list).
  clear() {
    this.queryTarget.value = ""
    this.hiddenTarget.value = ""
    delete this.hiddenTarget.dataset.label
    this.hiddenTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this.close()
  }
}
